import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import './Account.css';
import { IconPro, IconChart, IconCheck, IconAlert } from '../components/Icons';
import { API_ENDPOINTS } from '../config';
import {
  readTraderPsychologyTipEnabled,
  writeTraderPsychologyTipEnabled,
} from '../utils/traderPsychologyTipPreference';
import { ADMIN_VIEW_OPTIONS, adminViewLabel } from '../utils/adminViewAs';
import { useBrandIdentity } from '../utils/brandIdentity';
import EmailReportsCard from '../components/EmailReportsCard';

const AUTH_TOKEN_KEY = 'risklo_auth_token';
const MAX_FEEDBACK_IMAGES = 3;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

function Account({ onNavigate, onUpgrade }) {
  const { appName } = useBrandIdentity();
  const location = useLocation();
  const {
    user,
    isPro,
    isMax,
    isVectorAdmin,
    isVectorAdminPlus,
    isSupport,
    isSales,
    isBeta,
    isDevMode,
    isAdminActual,
    adminViewAs,
    setAdminViewAs,
    isAdminViewPreview,
    signOut,
    updateDefaultConsistencyPercent,
  } = useAuth();
  const showAdminTools = isAdminActual && adminViewAs === 'admin';
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [manageSubscriptionError, setManageSubscriptionError] = useState(null);
  
  // Email Summaries toggle state
  const [emailSummariesEnabled, setEmailSummariesEnabled] = useState(true);
  const [traderPsychologyTipsEnabled, setTraderPsychologyTipsEnabled] = useState(true);
  const [consistencyPercent, setConsistencyPercent] = useState(50);
  const [savingConsistencyPercent, setSavingConsistencyPercent] = useState(false);
  const [consistencySaveError, setConsistencySaveError] = useState(null);

  // Beta feedback state
  const [feedbackTitle, setFeedbackTitle] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackImages, setFeedbackImages] = useState([]);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);

  // My feedback history (Beta/Max)
  const [myFeedback, setMyFeedback] = useState([]);
  const [myFeedbackLoading, setMyFeedbackLoading] = useState(false);
  const [myFeedbackExpanded, setMyFeedbackExpanded] = useState(false);

  // Admin beta invite state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  
  // Fetch my feedback history when expanded (Beta/Max only)
  useEffect(() => {
    if (!myFeedbackExpanded || !(isBeta || isMax) || !user?.email) return;
    setMyFeedbackLoading(true);
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    fetch(API_ENDPOINTS.feedback, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.feedback)) {
          setMyFeedback(data.feedback);
        }
      })
      .catch(() => setMyFeedback([]))
      .finally(() => setMyFeedbackLoading(false));
  }, [myFeedbackExpanded, isBeta, isMax, user?.email]);

  // Load email summaries preference on mount
  useEffect(() => {
    if (user?.email) {
      const preferenceKey = `email_summaries_enabled_${user.email}`;
      const savedPreference = localStorage.getItem(preferenceKey);
      // Default to true (enabled) if not set
      setEmailSummariesEnabled(savedPreference !== 'false');
      setTraderPsychologyTipsEnabled(readTraderPsychologyTipEnabled(user.email));
    }
  }, [user]);

  useEffect(() => {
    const nextPercent = Number(user?.defaultConsistencyPercent);
    setConsistencyPercent([20, 30, 40, 50, 60].includes(nextPercent) ? nextPercent : 50);
  }, [user?.defaultConsistencyPercent]);

  useEffect(() => {
    const raw = (location.hash || '').replace(/^#/, '');
    if (raw !== 'email-summaries') return;
    const t = window.setTimeout(() => {
      document.getElementById('email-summaries')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => clearTimeout(t);
  }, [location.hash, location.pathname]);
  
  // Handle email summaries toggle
  const handleEmailSummariesToggle = (enabled) => {
    if (!isPro && !isMax && !isBeta) return; // Only allow Pro, Max, or Beta users to toggle
    if (user?.email) {
      const preferenceKey = `email_summaries_enabled_${user.email}`;
      localStorage.setItem(preferenceKey, enabled.toString());
      setEmailSummariesEnabled(enabled);
    }
  };

  const handleTraderPsychologyToggle = (enabled) => {
    if (!user?.email) return;
    writeTraderPsychologyTipEnabled(user.email, enabled);
    setTraderPsychologyTipsEnabled(enabled);
  };

  const handleConsistencyPercentChange = async (nextPercent) => {
    if (!user?.email || savingConsistencyPercent || nextPercent === consistencyPercent) return;
    setConsistencySaveError(null);
    setSavingConsistencyPercent(true);
    try {
      await updateDefaultConsistencyPercent(nextPercent);
      // Keep app-level selector in sync with account default immediately.
      const appSelectionKey = `risklo_selected_consistency_percent_${user.email}`;
      localStorage.setItem(appSelectionKey, String(nextPercent));
      setConsistencyPercent(nextPercent);
    } catch (error) {
      setConsistencySaveError(error.message || 'Failed to save default consistency %');
    } finally {
      setSavingConsistencyPercent(false);
    }
  };

  const handleSignInClick = () => {
    window.location.href = '/login';
  };

  const handleFeedbackImageChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_FEEDBACK_IMAGES);
    const valid = files.filter((f) => f.type.startsWith('image/') && f.size <= MAX_IMAGE_BYTES);
    setFeedbackImages(valid);
  };

  const handleSubmitFeedback = async () => {
    const msg = (feedbackMessage || '').trim();
    if (!msg || msg.length < 3) {
      setFeedbackError('Please enter at least a few characters');
      return;
    }
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    setFeedbackSuccess(false);
    try {
      const images = await Promise.all(
        feedbackImages.slice(0, MAX_FEEDBACK_IMAGES).map(async (file) => {
          const base64 = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => {
              const data = r.result;
              resolve(data ? data.split(',')[1] || '' : '');
            };
            r.onerror = reject;
            r.readAsDataURL(file);
          });
          return { filename: file.name, content: base64, type: file.type };
        })
      );
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(API_ENDPOINTS.feedback, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ title: (feedbackTitle || '').trim() || undefined, message: msg, images }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to submit feedback');
      }
      setFeedbackSuccess(true);
      setFeedbackTitle('');
      setFeedbackMessage('');
      setFeedbackImages([]);
    } catch (err) {
      setFeedbackError(err.message || 'Failed to submit feedback');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    setManageSubscriptionError(null);
    try {
      const response = await fetch(API_ENDPOINTS.subscriptionManage);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.manageUrl) {
        throw new Error(data.error || 'Subscription management link is unavailable right now.');
      }
      window.open(data.manageUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening subscription management:', error);
      setManageSubscriptionError(error.message || 'Could not open subscription management.');
    } finally {
      setManagingSubscription(false);
    }
  };

  const handleSendBetaInvite = async () => {
    const email = String(inviteEmail || '').trim().toLowerCase();
    const name = String(inviteName || '').trim();
    if (!email || !email.includes('@')) {
      setInviteError('Enter a valid email address.');
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setInviteError('Admin session missing. Please sign in again.');
      return;
    }

    setInviteSending(true);
    setInviteError(null);
    setInviteSuccess(null);
    try {
      const response = await fetch(API_ENDPOINTS.adminBetaInvite, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email, name }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to send beta invite');
      }
      setInviteSuccess(data?.message || `Welcome email sent to ${email}`);
      setInviteEmail('');
      setInviteName('');
    } catch (error) {
      setInviteError(error.message || 'Failed to send beta invite');
    } finally {
      setInviteSending(false);
    }
  };

  if (!user) {
    return (
      <div className="account-page">
        <PageHeader onNavigate={onNavigate} />
        <div className="account-content">
          <div className="account-card">
            <h2>Please Sign In</h2>
            <p>You need to sign in to view your account details.</p>
            <div className="account-sign-in-container" style={{ marginTop: '2rem' }}>
              <button 
                className="account-sign-in-button"
                onClick={handleSignInClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#fff',
                  color: '#000',
                  border: '1px solid #dadce0',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
              >
                <img
                  src="/RiskLo_LogoOnly.png"
                  alt=""
                  aria-hidden="true"
                  style={{ width: '18px', height: '18px', objectFit: 'contain' }}
                />
                Sign in to {appName}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="account-page">
      <PageHeader onNavigate={onNavigate} />
      <div className="account-content">
        <div className="account-card">
          <h1>Account</h1>
          
          <div className="account-profile-section">
            <div className="account-avatar">
              <span className="account-initials">{initials}</span>
            </div>
            <div className="account-profile-info">
              <h2>{user.name}</h2>
              <p className="account-email">{user.email}</p>
            </div>
          </div>

          <div className="account-plan-section">
            <h3>Current Plan</h3>

            {isAdminActual && (
              <div className="admin-view-switcher">
                <div className="admin-view-switcher-head">
                  <h4>Admin view preview</h4>
                  <p>
                    You are a platform admin. Choose how the app should look for testing — API access
                    stays admin until you switch back to Admin here.
                  </p>
                </div>
                {[
                  { group: 'company', title: 'Company member', hint: 'A member of the company you have selected in the sidebar.' },
                  { group: 'self', title: 'Self-use (no company)', hint: 'Someone who signed up on their own: RiskLo home look, the welcome Overview and their own accounts.' },
                ].map((g) => (
                  <div key={g.group} className="admin-view-switcher-group">
                    <div className="admin-view-switcher-group-title">
                      <strong>{g.title}</strong>
                      <span>{g.hint}</span>
                    </div>
                    <div className="admin-view-switcher-options" role="radiogroup" aria-label={`Preview as ${g.title}`}>
                      {ADMIN_VIEW_OPTIONS.filter((option) => option.group === g.group).map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          role="radio"
                          aria-checked={adminViewAs === option.id}
                          className={`admin-view-switcher-btn ${adminViewAs === option.id ? 'active' : ''}`}
                          onClick={() => setAdminViewAs(option.id)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {isAdminViewPreview && (
                  <p className="admin-view-switcher-note">
                    Previewing as <strong>{adminViewLabel(adminViewAs)}</strong>.
                    {adminViewAs === 'vadmin' || adminViewAs === 'vadminPlus' ? (
                      <> Staff tools (Strategies, Strategy Data, etc.) stay available in the sidebar.</>
                    ) : (
                      <>
                        {' '}Admin sidebar tools are hidden until you select <strong>Admin</strong> above.
                      </>
                    )}
                  </p>
                )}
              </div>
            )}

            <div className={`plan-badge-large ${
              isVectorAdmin ? 'vector-admin-plan' :
              isMax ? 'max-plan' :
              isBeta ? 'beta-plan' :
              isPro ? 'pro-plan' :
              'basic-plan'
            } ${isDevMode ? 'dev-plan' : ''}`}>
              <span className="plan-icon">{isMax || isPro ? <IconPro size={20} /> : <IconChart size={20} />}</span>
              <span className="plan-name">{
                isVectorAdminPlus ? `${appName} Brand Admin Plus` :
                isVectorAdmin ? `${appName} Brand Admin` :
                isSupport ? `${appName} Support` :
                isSales ? `${appName} Sales` :
                isMax ? (isDevMode ? `${appName} Max (Dev Mode)` : `${appName} Max`) :
                isBeta ? (isDevMode ? `${appName} Beta (Dev Mode)` : `${appName} Beta`) :
                isPro ? (isDevMode ? `${appName} Pro (Dev Mode)` : `${appName} Pro`) :
                `${appName} Basic`
              }</span>
            </div>

            {/* Terms Acceptance Status */}
            {(() => {
              const acceptanceKey = `disclaimer_accepted_${user.email}`;
              const dateKey = `disclaimer_accepted_date_${user.email}`;
              const hasAccepted = localStorage.getItem(acceptanceKey) === 'true';
              const acceptanceDate = localStorage.getItem(dateKey);
              
              if (hasAccepted) {
                return (
                  <div className="terms-acceptance-section" style={{
                    marginTop: '1.5rem',
                    padding: '1rem',
                    background: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.3)',
                    borderRadius: '8px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '0.9rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span><IconCheck size={16} /></span>
                      <strong>
                        <a 
                          href="#terms" 
                          onClick={(e) => {
                            e.preventDefault();
                            if (onNavigate) onNavigate('terms-and-conditions');
                          }}
                          style={{
                            color: 'inherit',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                          onMouseLeave={(e) => e.target.style.opacity = '1'}
                        >
                          Terms & Conditions Accepted
                        </a>
                      </strong>
                    </div>
                    {acceptanceDate && (
                      <div style={{ fontSize: '0.85rem', opacity: 0.8, marginLeft: '1.5rem' }}>
                        Accepted on {new Date(acceptanceDate).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })()}
            
            {isDevMode && (
              <div className="dev-mode-notice" style={{
                background: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginTop: '1rem',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.9rem'
              }}>
                <IconAlert size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} /> <strong>Dev Mode Active:</strong> You have {isMax ? 'Max' : isBeta ? 'Beta' : 'Pro'} access for testing purposes. This is not a paid subscription.
              </div>
            )}
            
            {!isMax && !isPro && (
              <div className="upgrade-section">
                <p className="upgrade-description">
                  Unlock advanced features like Bulk Risk Calculator and CSV uploads.
                </p>
                <button className="upgrade-button" onClick={onUpgrade}>
                  Upgrade to {appName} Pro
                </button>
              </div>
            )}

            {isMax && (
              <>
                <div className="pro-features">
                  <h4>Max Features Active:</h4>
                  <ul>
                    <li><IconCheck size={14} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} /> All Pro Features</li>
                    <li><IconCheck size={14} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} /> Portfolio Tracker (Broker Sync)</li>
                    <li><IconCheck size={14} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} /> Historical Strategy Performance</li>
                  </ul>
                </div>
              </>
            )}

            {isPro && !isMax && (
              <>
                <div className="pro-features">
                  <h4>Pro Features Active:</h4>
                  <ul>
                    <li><IconCheck size={14} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} /> Bulk Risk Calculator (up to 40 accounts)</li>
                    <li><IconCheck size={14} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} /> NinjaTrader CSV Upload</li>
                    <li><IconCheck size={14} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} /> Advanced risk analysis</li>
                  </ul>
                </div>
              </>
            )}

            {/* Email Summaries Toggle - for Pro, Max, and Beta */}
            <div className="email-summaries-section" style={{
                marginTop: '2rem',
                padding: '1.5rem',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#ffffff', fontSize: '1rem', fontWeight: 600 }}>
                      Default Consistency Rule
                    </h4>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', margin: 0 }}>
                      Sets your starting consistency % across analysis screens
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {[20, 30, 40, 50, 60].map((percent) => (
                      <button
                        key={percent}
                        type="button"
                        className={`account-consistency-btn${consistencyPercent === percent ? ' is-active' : ''}`}
                        onClick={() => handleConsistencyPercentChange(percent)}
                        disabled={savingConsistencyPercent}
                      >
                        {percent}%
                      </button>
                    ))}
                  </div>
                </div>
                {consistencySaveError ? (
                  <p style={{ margin: '0.75rem 0 0', color: '#ff8a80', fontSize: '0.85rem' }}>{consistencySaveError}</p>
                ) : null}
              </div>

            {user && (
              <div
                id="email-summaries"
                className="email-summaries-section"
                style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                }}
              >
                {(isPro || isMax || isBeta) && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#ffffff', fontSize: '1rem', fontWeight: 600 }}>
                        Email Summaries
                      </h4>
                      <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', margin: 0 }}>
                        Receive email summaries after CSV analysis
                      </p>
                    </div>
                    <label className="email-summaries-toggle">
                      <input
                        type="checkbox"
                        checked={emailSummariesEnabled}
                        onChange={(e) => handleEmailSummariesToggle(e.target.checked)}
                      />
                      <span className="toggle-slider">
                        <span className="toggle-slider-button" />
                      </span>
                    </label>
                  </div>
                )}
                <div
                  style={{
                    marginTop: (isPro || isMax || isBeta) ? '1.5rem' : 0,
                    paddingTop: (isPro || isMax || isBeta) ? '1.5rem' : 0,
                    borderTop: (isPro || isMax || isBeta) ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '1rem',
                  }}
                >
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#ffffff', fontSize: '1rem', fontWeight: 600 }}>
                      Trader psychology tips
                    </h4>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', margin: 0 }}>
                      Show rotating tips at the top of the site. You can hide them anytime with the X on the bar.
                    </p>
                  </div>
                  <label className="email-summaries-toggle">
                    <input
                      type="checkbox"
                      checked={traderPsychologyTipsEnabled}
                      onChange={(e) => handleTraderPsychologyToggle(e.target.checked)}
                    />
                    <span className="toggle-slider">
                      <span className="toggle-slider-button" />
                    </span>
                  </label>
                </div>
              </div>
            )}

                {isPro && !isMax && !isDevMode && (
                  <div className="subscription-management-section" style={{
                    marginTop: '2rem',
                    padding: '1.5rem',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px'
                  }}>
                    <h4 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Subscription Management</h4>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                      Billing is managed through Whop. Use the link below to manage your plan.
                    </p>
                    {manageSubscriptionError && (
                      <div style={{
                        padding: '0.75rem',
                        background: 'rgba(244, 67, 54, 0.1)',
                        border: '1px solid rgba(244, 67, 54, 0.3)',
                        borderRadius: '6px',
                        color: 'rgba(255, 255, 255, 0.9)',
                        marginBottom: '1rem',
                        fontSize: '0.9rem'
                      }}>
                        <IconAlert size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                        {manageSubscriptionError}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleManageSubscription}
                      disabled={managingSubscription}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        color: '#ffffff',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        cursor: managingSubscription ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        opacity: managingSubscription ? 0.7 : 1
                      }}
                    >
                      {managingSubscription ? 'Opening Whop...' : 'Manage Subscription in Whop'}
                    </button>
                  </div>
                )}
          </div>

          {(isBeta || isMax) && user && (
            <div className="beta-feedback-section">
              <h4>Beta Feedback</h4>
              <p className="feedback-description">
                Share feedback or screenshots to help improve {appName}.
              </p>
              <input
                type="text"
                className="feedback-title-input"
                placeholder="Title (optional – e.g., &quot;Bug: CSV upload fails&quot;)"
                value={feedbackTitle}
                onChange={(e) => setFeedbackTitle(e.target.value)}
                maxLength={120}
              />
              <textarea
                className="feedback-textarea"
                placeholder="Describe what you like, what could be better, or any bugs you've found..."
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                rows={4}
              />
              <div style={{ marginBottom: '1rem' }}>
                <label className="feedback-image-label">
                  Screenshots (optional, max 3, 2MB each)
                </label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  multiple
                  onChange={handleFeedbackImageChange}
                  style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.85rem' }}
                />
                {feedbackImages.length > 0 && (
                  <span className="feedback-image-count">
                    {feedbackImages.length} selected
                  </span>
                )}
              </div>
              {feedbackSuccess && (
                <div className="feedback-message-success">
                  <IconCheck size={16} />
                  Thanks for your feedback!
                </div>
              )}
              {feedbackError && (
                <div className="feedback-message-error">
                  <IconAlert size={16} />
                  {feedbackError}
                </div>
              )}
              <button
                className="feedback-submit-btn"
                onClick={handleSubmitFeedback}
                disabled={feedbackSubmitting || !(feedbackMessage || '').trim()}
              >
                {feedbackSubmitting ? 'Sending...' : 'Send Feedback'}
              </button>

              <div className="my-feedback-history" style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button
                  type="button"
                  onClick={() => setMyFeedbackExpanded(!myFeedbackExpanded)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#7dd3fc',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    padding: 0,
                  }}
                >
                  {myFeedbackExpanded ? '−' : '+'} Your feedback history
                  {myFeedbackExpanded && (myFeedbackLoading ? ' (loading...)' : ` (${myFeedback.length})`)}
                </button>
                {myFeedbackExpanded && (
                  <div style={{ marginTop: '0.75rem' }}>
                    {myFeedbackLoading ? (
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>Loading...</p>
                    ) : myFeedback.length === 0 ? (
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>No feedback submissions yet.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {myFeedback.map((f) => (
                          <div
                            key={f.id}
                            style={{
                              padding: '0.75rem',
                              background: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '6px',
                              fontSize: '0.9rem',
                            }}
                          >
                            <div style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '0.25rem' }}>
                              {new Date(f.created_at).toLocaleString()} • {f.status || 'active'}
                              {f.title && ` • ${f.title}`}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.9)', whiteSpace: 'pre-wrap' }}>{f.message}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {showAdminTools && (
            <div className="admin-invite-section">
              <h4>Admin: Send Beta Welcome Email</h4>
              <p className="admin-invite-description">
                Send a one-time account setup email with instructions and a secure password setup link.
              </p>
              <div className="admin-invite-grid">
                <input
                  type="email"
                  className="admin-invite-input"
                  placeholder="Member email (Whop)"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
                <input
                  type="text"
                  className="admin-invite-input"
                  placeholder="Name (optional)"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                />
              </div>
              {inviteSuccess ? <div className="admin-invite-success">{inviteSuccess}</div> : null}
              {inviteError ? <div className="admin-invite-error">{inviteError}</div> : null}
              <button
                type="button"
                className="admin-invite-button"
                onClick={handleSendBetaInvite}
                disabled={inviteSending}
              >
                {inviteSending ? 'Sending...' : 'Send Welcome Email'}
              </button>
            </div>
          )}

          {/* The Whop member roster moved to Admin → Entities, where it opens
              per brand from that brand's card. It used to live here hardcoded to
              Vector's company, which had no place to show any other brand. */}

          <EmailReportsCard onUpgrade={onUpgrade} showAdminSummary={showAdminTools} />

          <div className="account-actions">
            <button className="sign-out-button" onClick={signOut}>
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Account;

